'use client';

import { useState, useCallback, useMemo, useRef } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DraftingCompass, Check } from 'lucide-react';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Skeleton } from '@/shared/ui/shadcn/skeleton';
import { toast } from '@/shared/lib/toast';
import { ProjectSubpageLayout } from '@/shared/ui';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { albumApi, type Album } from '../api/album-service';
import { shareAlbumFile } from '../lib/share-album';
import { AlbumListRow } from './AlbumListRow';

type SortOrder = 'newest' | 'oldest';

function useSortOrder(): [SortOrder, (order: SortOrder) => void] {
  const [order, setOrder] = useState<SortOrder>(() => {
    if (typeof window !== 'undefined') {
      return (
        (localStorage.getItem('album-sort-order') as SortOrder) || 'newest'
      );
    }
    return 'newest';
  });

  const setSortOrder = useCallback((newOrder: SortOrder) => {
    setOrder(newOrder);
    if (typeof window !== 'undefined') {
      localStorage.setItem('album-sort-order', newOrder);
    }
  }, []);

  return [order, setSortOrder];
}

interface ProjectAlbumsPageProps {
  projectId: string;
}

export function ProjectAlbumsPage({ projectId }: ProjectAlbumsPageProps) {
  const t = useTranslations('album');
  const tProject = useTranslations('project');
  const router = useRouter();
  const queryClient = useQueryClient();
  const [sortOrder] = useSortOrder();

  const { data: entries = [], isLoading } = useQuery({
    queryKey: cacheKeys.projectAlbums.list(projectId),
    queryFn: () => albumApi.listByProject(projectId),
    enabled: !!projectId,
  });

  // Track pending deletes for undo
  const undoTimerRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const [pendingDeletes, setPendingDeletes] = useState<Set<string>>(new Set());

  const handleDelete = useCallback(
    (conceptId: string, albumId: string) => {
      // Optimistically hide the album
      setPendingDeletes((prev) => new Set(prev).add(albumId));

      // Actually delete on the server
      albumApi.delete(conceptId, albumId).catch(() => {
        // If delete fails, restore the album
        setPendingDeletes((prev) => {
          const next = new Set(prev);
          next.delete(albumId);
          return next;
        });
        toast.error(t('albumDeleted'));
      });

      // Show toast with undo
      const timer = setTimeout(() => {
        // After timeout, finalize: remove from pending and invalidate cache
        setPendingDeletes((prev) => {
          const next = new Set(prev);
          next.delete(albumId);
          return next;
        });
        undoTimerRef.current.delete(albumId);
        queryClient.invalidateQueries({
          queryKey: cacheKeys.projectAlbums.list(projectId),
        });
      }, 6000);

      undoTimerRef.current.set(albumId, timer);

      toast.info(t('albumDeletedUndo'), {
        action: {
          label: t('undo'),
          onClick: () => {
            // Cancel the finalization timer
            const existingTimer = undoTimerRef.current.get(albumId);
            if (existingTimer) {
              clearTimeout(existingTimer);
              undoTimerRef.current.delete(albumId);
            }

            // Restore on server
            albumApi
              .restore(conceptId, albumId)
              .then(() => {
                setPendingDeletes((prev) => {
                  const next = new Set(prev);
                  next.delete(albumId);
                  return next;
                });
                queryClient.invalidateQueries({
                  queryKey: cacheKeys.projectAlbums.list(projectId),
                });
                toast.success(t('albumRestored'));
              })
              .catch(() => {
                toast.error(t('albumDeleted'));
              });
          },
        },
      });
    },
    [t, queryClient, projectId]
  );

  const handleDownload = useCallback((album: Album) => {
    if (album.public_url) {
      window.open(album.public_url, '_blank');
    }
  }, []);

  const handleCopyLink = useCallback(
    async (publicUrl: string | undefined) => {
      if (!publicUrl) {
        toast.error(t('publicUrlNotAvailable'));
        return;
      }
      try {
        await navigator.clipboard.writeText(publicUrl);
        toast.success(t('publicLinkCopied'), {
          icon: <Check className="h-4 w-4" />,
        });
      } catch {
        toast.error(t('failedToCopyLink'));
      }
    },
    [t]
  );

  // Shares the PDF file (copying the link is the separate chain icon). Degrades
  // to a URL share, then clipboard copy, when file share is unsupported.
  const handleShare = useCallback(
    async (album: Album) => {
      if (!album.public_url) {
        toast.error(t('publicUrlNotAvailable'));
        return;
      }
      const result = await shareAlbumFile(album.public_url, album.filename);
      if (result === 'fallback') {
        await handleCopyLink(album.public_url);
      }
    },
    [t, handleCopyLink]
  );

  // Sort entries by concept sequence number
  const sortedEntries = useMemo(() => {
    return [...entries].sort((a, b) => {
      const diff =
        a.concept.project_sequence_number - b.concept.project_sequence_number;
      return sortOrder === 'newest' ? -diff : diff;
    });
  }, [entries, sortOrder]);

  // Filter out pending deletes from display
  const visibleEntries = useMemo(() => {
    if (pendingDeletes.size === 0) return sortedEntries;
    return sortedEntries
      .map((entry) => ({
        ...entry,
        albums: entry.albums.filter((album) => !pendingDeletes.has(album.id)),
      }))
      .filter((entry) => entry.albums.length > 0);
  }, [sortedEntries, pendingDeletes]);

  return (
    <ProjectSubpageLayout projectId={projectId} backLabel={tProject('backToProject')} title={t('albums')}>
      {/* Content */}
      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="aspect-[4/3] w-full rounded-xl" />
          ))}
        </div>
      ) : entries.length === 0 ? (
        <EmptyState
          icon={<DraftingCompass />}
          title={t('noAlbums')}
          description={t.rich('noAlbumsHint', {
            link: (chunks) => (
              <Link
                href={`/project/${projectId}/concepts`}
                className="text-primary underline-offset-4 hover:underline"
              >
                {chunks}
              </Link>
            ),
          })}
          centered
        />
      ) : (
        // One tile per row on phones, 2 / 3 / 4 as the viewport widens —
        // mirrors ProjectEstimatesPage (Android/Apple parity: adaptive grid
        // with a ~270dp minimum tile). In a grid cell (<800px container)
        // AlbumListRow renders its vertical card with inline action buttons.
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {visibleEntries.map((entry) =>
            entry.albums.map((album) => (
              <AlbumListRow
                key={album.id}
                album={album}
                conceptTitle={entry.concept.title}
                conceptNumber={entry.concept.project_sequence_number}
                coverImageUrl={entry.concept.main_render_url}
                onDelete={
                  album.status === 'completed' || album.status === 'failed'
                    ? () => handleDelete(entry.concept.id, album.id)
                    : undefined
                }
                onDownload={() => handleDownload(album)}
                // Tile click navigates to the concept's Album tab (same as
                // the project-page and dashboard carousels); the PDF opens
                // from the concept page or via the explicit download button.
                onTap={() =>
                  router.push(
                    `/project/${projectId}/concept/${entry.concept.id}?tab=album`
                  )
                }
                onCopyLink={() => handleCopyLink(album.public_url)}
                onShare={() => handleShare(album)}
              />
            ))
          )}
        </div>
      )}
    </ProjectSubpageLayout>
  );
}
